import { useEffect, useRef, useState } from 'react';
import { X, Tag as TagIcon, Plus, Check, ListChecks } from 'lucide-react';
import clsx from 'clsx';
import { api } from '../lib/api';
import type { CardRow, Priority } from '../types';

const PRIORITY_OPTIONS: Array<{ value: Priority; label: string; color: string }> = [
  { value: 'low', label: 'Low', color: '#9ca3af' },
  { value: 'med', label: 'Med', color: '#3b82f6' },
  { value: 'high', label: 'High', color: '#f59e0b' },
  { value: 'critical', label: 'Critical', color: '#ef4444' },
];

interface TaskModalProps {
  cardId: string;
  onClose: () => void;
  boardCards: CardRow[];
}

export function TaskModal({ cardId, onClose }: TaskModalProps) {
  const fullQuery = api.cards.getFull.useQuery({ cardId });
  const utils = api.useUtils();
  const updateMutation = api.cards.update.useMutation({
    onSuccess: () => {
      utils.cards.getFull.invalidate({ cardId });
      const boardId = fullQuery.data?.card.boardId;
      if (boardId) utils.boards.getFull.invalidate({ boardId });
    },
  });

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  // ID последней загруженной карточки — чтобы НЕ перезаписывать local state
  // (typed text) при каждом invalidate/refetch той же карточки. Reset
  // только когда сменили карту (другой cardId или первая загрузка).
  const loadedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!fullQuery.data) return;
    const card = fullQuery.data.card;
    if (loadedIdRef.current === card.id) return; // уже загружали — не трём ввод
    loadedIdRef.current = card.id;
    setTitle(card.title);
    setDescription(card.description ?? '');
  }, [fullQuery.data]);

  // Esc — закрыть
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  if (!fullQuery.data) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="bg-surface-light dark:bg-surface-dark rounded-lg p-8">Загрузка…</div>
      </div>
    );
  }

  const { card, comments, checklists, tags } = fullQuery.data;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface-light dark:bg-surface-dark rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-5 py-3 border-b border-line-light dark:border-line-dark">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => {
              if (title !== card.title) updateMutation.mutate({ id: cardId, title });
            }}
            className="flex-1 text-lg font-semibold bg-transparent focus:outline-none"
          />
          <button onClick={onClose} className="ml-4 opacity-60 hover:opacity-100">
            <X size={20} />
          </button>
        </header>

        <div className="flex-1 overflow-auto grid grid-cols-[1fr_280px] gap-0">
          {/* Левая часть */}
          <div className="p-5 border-r border-line-light dark:border-line-dark space-y-5">
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-muted_l dark:text-ink-muted_d mb-1.5">
                Описание
              </h3>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onBlur={() => {
                  if (description !== card.description)
                    updateMutation.mutate({ id: cardId, description });
                }}
                placeholder="Опиши задачу подробнее…"
                rows={5}
                className="w-full px-3 py-2 text-sm rounded border border-line-light dark:border-line-dark bg-surface2-light dark:bg-surface2-dark focus:outline-none focus:ring-1 focus:ring-accent resize-y"
              />
            </section>

            <ChecklistsSection cardId={cardId} checklists={checklists} />
            <CommentsSection cardId={cardId} comments={comments} />
          </div>

          {/* Правая часть — sidebar */}
          <aside className="p-5 bg-surface2-light dark:bg-surface2-dark space-y-4 text-sm">
            <Field label="Приоритет">
              <div className="flex gap-1">
                {PRIORITY_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => updateMutation.mutate({ id: cardId, priority: opt.value })}
                    className={clsx(
                      'flex-1 px-2 py-1 text-xs rounded transition-colors',
                      card.priority === opt.value
                        ? 'ring-2 ring-offset-1 ring-offset-surface2-light dark:ring-offset-surface2-dark text-white font-semibold'
                        : 'opacity-70 hover:opacity-100',
                    )}
                    style={{
                      backgroundColor: card.priority === opt.value ? opt.color : `${opt.color}33`,
                      color: card.priority === opt.value ? 'white' : opt.color,
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </Field>

            <Field label="Дедлайн">
              <input
                type="date"
                value={card.dueDate ? card.dueDate.slice(0, 10) : ''}
                onChange={(e) =>
                  updateMutation.mutate({
                    id: cardId,
                    dueDate: e.target.value ? new Date(e.target.value).toISOString() : null,
                  })
                }
                className="w-full px-2 py-1 text-sm rounded border border-line-light dark:border-line-dark bg-surface-light dark:bg-surface-dark"
              />
            </Field>

            <Field label="Story Points">
              <input
                type="number"
                step="0.5"
                value={card.estimate ?? ''}
                onChange={(e) =>
                  updateMutation.mutate({
                    id: cardId,
                    estimate: e.target.value ? Number(e.target.value) : null,
                  })
                }
                className="w-full px-2 py-1 text-sm rounded border border-line-light dark:border-line-dark bg-surface-light dark:bg-surface-dark"
                placeholder="—"
              />
            </Field>

            <Field label="Теги">
              <TagsEditor cardId={cardId} tags={tags} />
            </Field>

            <div className="text-xs text-ink-faint_l dark:text-ink-faint_d pt-2 border-t border-line-light dark:border-line-dark">
              ID: {cardId.slice(0, 8)}…
              <br />
              Создана: {new Date(card.createdAt).toLocaleString('ru-RU')}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-wide text-ink-muted_l dark:text-ink-muted_d mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

function ChecklistsSection({
  cardId,
  checklists,
}: {
  cardId: string;
  checklists: Array<{ id: string; title: string; items: Array<{ id: string; text: string; done: boolean }> }>;
}) {
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const utils = api.useUtils();
  const addChecklist = api.cards.addChecklist.useMutation({
    onSuccess: () => {
      setNewTitle('');
      setAdding(false);
      utils.cards.getFull.invalidate({ cardId });
    },
  });
  const addItem = api.cards.addChecklistItem.useMutation({
    onSuccess: () => utils.cards.getFull.invalidate({ cardId }),
  });
  const toggleItem = api.cards.toggleChecklistItem.useMutation({
    onSuccess: () => utils.cards.getFull.invalidate({ cardId }),
  });
  const convertMutation = api.cards.convertChecklistToSubtasks.useMutation({
    onSuccess: () => {
      utils.cards.getFull.invalidate({ cardId });
      utils.boards.getFull.invalidate();
    },
  });

  return (
    <section>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-muted_l dark:text-ink-muted_d mb-2">
        Чек-листы
      </h3>
      {checklists.map((cl) => (
        <div key={cl.id} className="mb-3">
          <div className="flex items-center justify-between mb-1.5">
            <h4 className="text-sm font-medium flex-1">{cl.title}</h4>
            <span className="text-xs text-ink-faint_l dark:text-ink-faint_d mr-2">
              {cl.items.filter((i) => i.done).length}/{cl.items.length}
            </span>
            {cl.items.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(`Превратить ${cl.items.length} пунктов чек-листа в отдельные подзадачи?`)) {
                    convertMutation.mutate({ checklistId: cl.id });
                  }
                }}
                className="text-[10px] text-accent hover:underline flex items-center gap-0.5"
                title="Превратить в подзадачи"
              >
                <ListChecks size={11} />
                В подзадачи
              </button>
            )}
          </div>
          <ul className="space-y-1">
            {cl.items.map((it) => (
              <li key={it.id} className="flex items-center gap-2 text-sm">
                <button
                  type="button"
                  onClick={() => toggleItem.mutate({ id: it.id, done: !it.done })}
                  className={clsx(
                    'w-4 h-4 rounded border border-line-light dark:border-line-dark flex items-center justify-center',
                    it.done && 'bg-accent border-accent',
                  )}
                >
                  {it.done && <Check size={11} className="text-white" />}
                </button>
                <span className={clsx(it.done && 'line-through text-ink-faint_l dark:text-ink-faint_d')}>
                  {it.text}
                </span>
              </li>
            ))}
          </ul>
          <ChecklistItemAdder onAdd={(text) => addItem.mutate({ checklistId: cl.id, text })} />
        </div>
      ))}
      {adding ? (
        <div className="flex gap-1">
          <input
            autoFocus
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Название чек-листа"
            onKeyDown={(e) => {
              if (e.key === 'Enter') addChecklist.mutate({ cardId, title: newTitle });
              if (e.key === 'Escape') setAdding(false);
            }}
            className="flex-1 px-2 py-1 text-sm rounded border border-line-light dark:border-line-dark bg-surface-light dark:bg-surface-dark"
          />
          <button
            onClick={() => addChecklist.mutate({ cardId, title: newTitle })}
            className="px-2 py-1 text-xs rounded bg-accent text-white"
          >
            Добавить
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="text-xs text-accent hover:underline flex items-center gap-1"
        >
          <Plus size={12} />
          Чек-лист
        </button>
      )}
    </section>
  );
}

function ChecklistItemAdder({ onAdd }: { onAdd: (text: string) => void }) {
  const [text, setText] = useState('');
  const [active, setActive] = useState(false);

  if (!active) {
    return (
      <button
        type="button"
        onClick={() => setActive(true)}
        className="mt-1 text-xs text-ink-faint_l dark:text-ink-faint_d hover:text-accent flex items-center gap-1"
      >
        <Plus size={11} /> Пункт
      </button>
    );
  }

  return (
    <input
      autoFocus
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        if (text.trim()) onAdd(text.trim());
        setText('');
        setActive(false);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          if (text.trim()) onAdd(text.trim());
          setText('');
        }
        if (e.key === 'Escape') {
          setText('');
          setActive(false);
        }
      }}
      placeholder="Новый пункт…"
      className="mt-1 w-full px-2 py-1 text-sm rounded border border-line-light dark:border-line-dark bg-surface-light dark:bg-surface-dark"
    />
  );
}

function CommentsSection({
  cardId,
  comments,
}: {
  cardId: string;
  comments: Array<{ id: string; content: string; createdAt: string; authorKind: string }>;
}) {
  const [text, setText] = useState('');
  const utils = api.useUtils();
  const addComment = api.cards.addComment.useMutation({
    onSuccess: () => {
      setText('');
      utils.cards.getFull.invalidate({ cardId });
    },
  });

  return (
    <section>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-muted_l dark:text-ink-muted_d mb-2">
        Комментарии ({comments.length})
      </h3>
      <div className="space-y-2 mb-3">
        {comments.map((c) => (
          <div
            key={c.id}
            className="rounded-md bg-surface2-light dark:bg-surface2-dark p-2 text-sm"
          >
            <div className="text-xs text-ink-faint_l dark:text-ink-faint_d mb-1">
              {c.authorKind === 'ai-agent' ? '🤖 AI Agent' : '👤 You'} ·{' '}
              {new Date(c.createdAt).toLocaleString('ru-RU')}
            </div>
            <div className="whitespace-pre-wrap">{c.content}</div>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={2}
          placeholder="Добавить комментарий…"
          className="flex-1 px-2 py-1.5 text-sm rounded border border-line-light dark:border-line-dark bg-surface2-light dark:bg-surface2-dark resize-y"
        />
        <button
          type="button"
          onClick={() => text.trim() && addComment.mutate({ cardId, content: text.trim() })}
          disabled={!text.trim()}
          className="px-3 text-sm rounded bg-accent text-white disabled:opacity-50"
        >
          Send
        </button>
      </div>
    </section>
  );
}

function TagsEditor({ cardId, tags }: { cardId: string; tags: string[] }) {
  const [newTag, setNewTag] = useState('');
  const utils = api.useUtils();
  const setTagsMutation = api.cards.setTags.useMutation({
    onSuccess: () => utils.cards.getFull.invalidate({ cardId }),
  });

  const addTag = () => {
    const t = newTag.trim();
    if (!t || tags.includes(t)) return;
    setTagsMutation.mutate({ cardId, tags: [...tags, t] });
    setNewTag('');
  };

  const removeTag = (t: string) => {
    setTagsMutation.mutate({ cardId, tags: tags.filter((x) => x !== t) });
  };

  return (
    <div>
      <div className="flex flex-wrap gap-1 mb-1.5">
        {tags.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => removeTag(t)}
            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-accent/10 text-accent hover:bg-red-500/20 hover:text-red-500"
          >
            <TagIcon size={10} />
            {t}
            <X size={10} />
          </button>
        ))}
      </div>
      <input
        value={newTag}
        onChange={(e) => setNewTag(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && addTag()}
        placeholder="Новый тег + Enter"
        className="w-full px-2 py-1 text-xs rounded border border-line-light dark:border-line-dark bg-surface-light dark:bg-surface-dark"
      />
    </div>
  );
}
